import { Alert, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ITooRequestCustomControlDataRedenVanVerwijzing } from '@common/validation/customControls/ophthalmology';
import type { IReduxState } from '@frontend/configureStore';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import {
    CustomControlMode,
    FilesUrlBase,
    PractitionerRoleInRequest,
    ProcessEntity,
    saveTypeEnum,
    ServiceCode,
    ToastType,
} from '@common/enums';
import { ServicePermissionsAuthorizations } from '@common/interfaces/servicePermissions';
import { PatientInsuranceOldCarepath } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsuranceOldCarepath';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { translateKey } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useEpisodeContext } from '@frontend/utils/hooks/useEpisodeContext';
import { showServiceToast } from '@frontend/utils/showToast';
import type { IMedication } from '../../../../interfaces/medication';
import type { IRequestAddEdit } from '../../../requests/RequestItemAddEdit';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import {
    ButtonContainer,
    Feedback,
    Form,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    InputTextArea,
    MediumInputText,
    SaveSubmit,
} from '../../../../elements/common';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { AssetDropzoneWithActions } from '../../utils';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { RequestCriteria } from './RequestCriteria';
import { TooV1FollowingSteps } from './TooV1FollowingSteps';

export function TooV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const {
        request: { customControlData },
    } = props;
    const { patient } = useEpisodeContext();
    const abortableFetch = useAbortableFetch();
    const abortSignal = useAbortSignal();
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string>();
    const [showOctAlert, setShowOctAlert] = useState(false);
    const [showRequestForm, setShowRequestForm] = useState(props.request.id ? true : false);
    const [showPatientEmailField, setShowPatientEmailField] = useState(false);
    const [isAndersChecked, setIsAndersChecked] = useState(!!customControlData.reden_van_verwijzing.andersText);
    const [redenVanVerwijzingError, setRedenVanVerwijzingError] = useState('');
    const [isAndersInvalid, setIsAndersInvalid] = useState(
        isAndersChecked && customControlData.reden_van_verwijzing.andersText === null
    );
    const [excludeNonOctPractices, setExcludeNonOctPractices] = useState(false);
    const [addOctPracticesInformation, setAddOctPracticesInformation] = useState(false);
    const {
        servicePermissions: { [ServiceCode.TOO]: tooPermissions },
    } = useSelector((state: IReduxState) => state.authentication);

    useEffect(() => {
        setIsAndersInvalid(isAndersChecked && customControlData.reden_van_verwijzing.andersText === null);
    }, [isAndersChecked, customControlData.reden_van_verwijzing.andersText]);

    useEffect(() => {
        if (!isAndersChecked && showRequestForm) {
            handleChangeRedenVanVerwijzing('andersText', null);
        }
    }, [isAndersChecked]);

    useEffect(() => {
        if (isAndersInvalid) {
            setRedenVanVerwijzingError('any_required');
        } else {
            setRedenVanVerwijzingError(props.errors.get('customControlData.reden_van_verwijzing') || '');
        }
    }, [props.errors, isAndersInvalid]);

    useEffect(() => {
        const nrOfReasons = Object.values(customControlData.reden_van_verwijzing).filter((value) => value).length;
        setExcludeNonOctPractices(customControlData.reden_van_verwijzing.risicofactoren_glaucoom && nrOfReasons === 1);
        setAddOctPracticesInformation(
            customControlData.reden_van_verwijzing.risicofactoren_glaucoom && nrOfReasons > 1
        );
    }, [customControlData.reden_van_verwijzing]);

    useEffect(() => {
        if (excludeNonOctPractices || addOctPracticesInformation) {
            if (props.request.selectedProcedureProviderOrganizationId) {
                checkCanProviderPerformGlaucoomExamination(
                    props.request.selectedProcedureProviderOrganizationId,
                    excludeNonOctPractices
                );
            }
        } else {
            setShowOctAlert(false);
        }
    }, [excludeNonOctPractices, addOctPracticesInformation, props.request.selectedProcedureProviderOrganizationId]);

    useEffect(() => {
        checkShouldShowPatientEmailField(props.request.selectedProcedureProviderOrganizationId);
    }, [props.request.selectedProcedureProviderOrganizationId]);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest={true}
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={
                showRequestForm
                    ? getServiceLabel(props.service.code, 'request', props.service.name)
                    : 'Oogonderzoek exclusiecriteria'
            }
        >
            {!showRequestForm && (
                <RequestCriteria
                    onCancel={props.onCancel}
                    onContinue={async () => setShowRequestForm(true)}
                    patient={patient}
                    service={props.service}
                />
            )}
            {showRequestForm && (
                <Form onSubmit={async () => await saveRequest(saveTypeEnum.final)}>
                    {addEditRequestForm()}

                    <TooV1FollowingSteps />

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!props.request.id}
                                key={'cancelButton'}
                                revertChanges={props.revertRequestChanges}
                                serviceCode={props.service.code}
                                serviceType={props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={isButtonDisabled || isUploading}
                                key={'saveButton'}
                                save={async () => {
                                    await saveRequest(saveTypeEnum.draft);
                                }}
                            />,
                            <SaveSubmit
                                disabled={isButtonDisabled || isUploading}
                                key={'submitButton'}
                                name={'request.saveSend'}
                            />,
                        ]}
                    />
                </Form>
            )}
        </NewOrEditRequestProcessContainer>
    );

    function addEditRequestForm() {
        return (
            <>
                <PatientInsuranceOldCarepath
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    isPatientInformed={props.request.customControlData.isPatientInformed}
                    mode={CustomControlMode.edit}
                    onCancel={props.onCancel}
                    serviceCode={ServiceCode.TOO}
                />
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <ServiceOrganizationProvider
                    addOctPracticesInformation={addOctPracticesInformation}
                    errors={props.errors}
                    excludeNonOctPractices={excludeNonOctPractices}
                    initialOrganizationId={props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={props.request.selectedProcedureProviderPractitionerId}
                    onChange={async (organizationId, practitionerId) => {
                        await handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceCode={ServiceCode.TOO}
                    serviceProviderServiceId={props.service.id}
                />

                {showPatientEmailField && (
                    <FormGroup
                        hasErrors={props.errors.has('customControlData.patientEmailAddress')}
                        label={translateKey('patient.patientEmailAddress')}
                        tooltipText="We sturen een uitnodiging voor MijnKsyos. Daar leest de patiënt over Ksyos Oogheelkunde en de volgende stappen. Later krijgt de patiënt hier ook de uitslag."
                    >
                        <MediumInputText
                            error={props.errors.get('customControlData.patientEmailAddress')}
                            onChange={async (value) => {
                                await props.handleChangeCustomControl({ patientEmailAddress: value || null });
                            }}
                            onDoValidate={async () => await props.validateOneCustomControl('patientEmailAddress')}
                            value={props.request.customControlData.patientEmailAddress}
                        />
                    </FormGroup>
                )}

                {fileUploadErrorMessage && <Alert appearance="warning">{fileUploadErrorMessage}</Alert>}
                {tooPermissions?.[ServicePermissionsAuthorizations.canAddAssets] && (
                    <FormGroup
                        hasErrors={Boolean(fileUploadErrorMessage)}
                        isRequired={true}
                        label={translateKey('request.referralLetter')}
                    >
                        <AssetDropzoneWithActions
                            acceptedFiles=".pdf"
                            assets={props.request?.customControlData.files ?? []}
                            assetsBaseUrl={FilesUrlBase.requests}
                            cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                            maxFiles={1}
                            onChange={async (assets) => {
                                await props.handleChangeCustomControl({ files: assets });
                            }}
                            onUploading={async (isUploading) => setIsUploading(isUploading)}
                            processActivityId={props.request.id}
                            service={props.service}
                        />
                    </FormGroup>
                )}

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={props.request.patientUsesMedication}
                    validateMedications={async () => await props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    errors={props.errors}
                    handleChangeAllergies={async (allergies) => await handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) => await handleChange('requestLabResults', labResults)}
                    validateEpisodesAndJournals={async () => await props.validateOne('requestEpisodesAndJournal')}
                />

                {showOctAlert && (
                    <Alert appearance="warning">
                        Let op: vul het ‘verstuur naar’ veld hierboven opnieuw. Niet alle organisaties beschikken over
                        een OCT scan voor het uitvoeren van een glaucoomonderzoek.
                    </Alert>
                )}

                <HeaderDivider text={translateKey('request.request')} />

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.reden_van_verwijzing')}>
                    <FormLabel isRequired>Reden van verwijzing</FormLabel>
                    <InputContainer>
                        <FlexColumns sm={12}>
                            <InputCheckbox
                                automation="hoofdpijn"
                                checked={customControlData.reden_van_verwijzing.hoofdpijn}
                                onChange={async (value) => await handleChangeRedenVanVerwijzing('hoofdpijn', value)}
                                text="Hoofdpijn"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.rood_oog_droog_oog}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('rood_oog_droog_oog', value)
                                }
                                text="Rood oog, droog oog"
                            />
                            <InputCheckbox
                                automation="visusveranderingenLangzaam"
                                checked={customControlData.reden_van_verwijzing.visusveranderingen}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('visusveranderingen', value)
                                }
                                text="Visusveranderingen (langzaam)"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.verdenking_op_cataract}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('verdenking_op_cataract', value)
                                }
                                text="Verdenking op cataract"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.wazig_zien}
                                onChange={async (value) => await handleChangeRedenVanVerwijzing('wazig_zien', value)}
                                text="Wazig zien"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.dubbel_zien}
                                onChange={async (value) => await handleChangeRedenVanVerwijzing('dubbel_zien', value)}
                                text="Dubbel zien"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.vervormd_zien}
                                onChange={async (value) => await handleChangeRedenVanVerwijzing('vervormd_zien', value)}
                                text="Vervormd zien"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.gezichtsveldklachten}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('gezichtsveldklachten', value)
                                }
                                text="Gezichtsveldklachten"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.prikken_jeuken_tranen}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('prikken_jeuken_tranen', value)
                                }
                                text="Prikken, jeuken, tranen"
                            />
                            <InputCheckbox
                                checked={customControlData.reden_van_verwijzing.risicofactoren_glaucoom}
                                onChange={async (value) =>
                                    await handleChangeRedenVanVerwijzing('risicofactoren_glaucoom', value)
                                }
                                text="Risicofactoren glaucoom"
                            />
                            <InputTextCheckbox
                                hasFormControl={true}
                                onChange={async (value) => {
                                    await handleChangeRedenVanVerwijzing('andersText', value);
                                    setIsAndersChecked(!!isAndersChecked);
                                }}
                                onDoValidate={async () => {
                                    props.validateOneCustomControl('reden_van_verwijzing.andersText');
                                }}
                                placeholder="Anders, namelijk..."
                                text={customControlData.reden_van_verwijzing.andersText}
                            />
                        </FlexColumns>
                        <Feedback error={redenVanVerwijzingError} />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup
                    hasErrors={props.errors.has('customControlData.omschrijf_de_duur_van_de_klachten')}
                >
                    <FormLabel> Duur van de klachten </FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.omschrijf_de_duur_van_de_klachten')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({ omschrijf_de_duur_van_de_klachten: value })
                            }
                            onDoValidate={async () =>
                                await props.validateOneCustomControl('omschrijf_de_duur_van_de_klachten')
                            }
                            value={customControlData.omschrijf_de_duur_van_de_klachten}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup
                    hasErrors={props.errors.has('customControlData.relevante_medische_voorgeschiedenis')}
                >
                    <FormLabel>Medische voorgeschiedenis</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.relevante_medische_voorgeschiedenis')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({ relevante_medische_voorgeschiedenis: value })
                            }
                            onDoValidate={async () =>
                                await props.validateOneCustomControl('relevante_medische_voorgeschiedenis')
                            }
                            value={customControlData.relevante_medische_voorgeschiedenis}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.oculaire_voorgeschiedenis')}>
                    <FormLabel>Oculaire voorgeschiedenis</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.oculaire_voorgeschiedenis')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({ oculaire_voorgeschiedenis: value })
                            }
                            onDoValidate={async () => await props.validateOneCustomControl('oculaire_voorgeschiedenis')}
                            value={customControlData.oculaire_voorgeschiedenis}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.familiaire_voorgeschiedenis')}>
                    <FormLabel>Familiaire voorgeschiedenis</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            error={props.errors.get('customControlData.familiaire_voorgeschiedenis')}
                            onChange={async (value) =>
                                await props.handleChangeCustomControl({ familiaire_voorgeschiedenis: value })
                            }
                            onDoValidate={async () =>
                                await props.validateOneCustomControl('familiaire_voorgeschiedenis')
                            }
                            value={customControlData.familiaire_voorgeschiedenis}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={props.errors.has('customControlData.toelichting')}>
                    <FormLabel>Toelichting</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="toelichtingRequest"
                            error={props.errors.get('customControlData.toelichting')}
                            onChange={async (value) => await props.handleChangeCustomControl({ toelichting: value })}
                            onDoValidate={async () => await props.validateOneCustomControl('toelichting')}
                            value={customControlData.toelichting}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    async function saveRequest(saveType: saveTypeEnum.draft | saveTypeEnum.final) {
        setIsButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setIsButtonDisabled(false);

            return;
        }

        if (
            tooPermissions?.canAddAssets &&
            saveType === 'final' &&
            (!props.request.customControlData.files || props.request.customControlData.files.length === 0)
        ) {
            setFileUploadErrorMessage(translateKey('dropzone.addReferralLetter'));
            setIsButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await props.onSave(requestId);
    }

    async function handleChange<K extends keyof IRequestAddEdit>(name: K, value: IRequestAddEdit[K]) {
        await props.handleChange(name, value);
    }

    async function handleChangeRedenVanVerwijzing<K extends keyof ITooRequestCustomControlDataRedenVanVerwijzing>(
        name: K,
        value: ITooRequestCustomControlDataRedenVanVerwijzing[K]
    ) {
        await props.handleChangeCustomControl({
            reden_van_verwijzing: {
                ...customControlData.reden_van_verwijzing,
                [name]: value,
            },
        });
        props.validateOneCustomControl('reden_van_verwijzing');
    }

    async function checkCanProviderPerformGlaucoomExamination(organizationId: string, resetSelectedProvider: boolean) {
        const url = `/api/serviceOrganizationSettings?serviceCode=TOO&isActive=true&organizationId=${organizationId}`;
        const serviceOrganizationSettings = (await abortableFetch.fetchJson(url, 'GET')).json.items;
        if (serviceOrganizationSettings.length > 0) {
            const canProvide = serviceOrganizationSettings[0].canPerformGlaucoomExamination;
            setShowOctAlert(!canProvide);
            if (!canProvide && resetSelectedProvider) {
                handleChange('selectedProcedureProviderOrganizationId', null);
                handleChange('selectedProcedureProviderPractitionerId', null);
            }
        }
    }

    async function checkShouldShowPatientEmailField(organizationId: string | null) {
        if (!organizationId) {
            return setShowPatientEmailField(false);
        }
        const organization = await getOneOrganization(organizationId, abortSignal);
        if (organization.isMijnKsyosActive) {
            props.request.customControlData.patientEmailAddress = patient.emailAddress;
            setShowPatientEmailField(true);
        } else {
            setShowPatientEmailField(false);
        }
    }
}
